import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import axios from "axios";
import Swal from "sweetalert2";

import { APP_API_URL } from "../../utils";
import OrderLayout from "../../layout/order-layout";
import Loader from "../../components/loader";
import DuoModal from "../../components/ui/duo-modal";
import FactureComponent from "../../components/facture";
import InstallmentOrderInvoice from "./installment-order-invoice";
import InstallmentOrderInvoiceMagasinier from "./installment-order-invoice-magasinier";
import formatNumber from "../../utils/functions";
import { stageInstallmentForCart } from "../commandes/cart-edit";

const PAYMENT_METHODS = [
  { label: "Mobile Money", value: 1 },
  { label: "Carte bancaire", value: 2 },
  { label: "Virement", value: 3 },
  { label: "Espèces", value: 4 },
  { label: "Crédit", value: 5 },
  { label: "Autre", value: 6 },
  { label: "Chèques", value: 7 },
];

const STATUS_LABELS = {
  pending: "En attente",
  partial: "Partiellement payé",
  paid: "Soldé",
  converted: "Reprise en vente",
};

export default function InstallmentInvoice() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const isRole13 = Number(user?.personnels?.[0]?.poste_id) === 13;
  const listPath = window.location.pathname.startsWith("/admin/")
    ? "/admin/installment-orders"
    : "/installment-orders";

  const [item, setItem] = useState({});
  const [isLoading, setLoading] = useState(true);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [activeView, setActiveView] = useState("discharge");

  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("Espèces");
  const [paymentNote, setPaymentNote] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [finalizeLoading, setFinalizeLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${APP_API_URL}/installment-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data.data;
      setItem(data);

      if (data.payments?.length > 0) {
        const mostRecentPayment = data.payments.reduce((latest, payment) =>
          new Date(payment.payment_date) > new Date(latest.payment_date) ? payment : latest
        );
        setSelectedPaymentId(mostRecentPayment.id);
      }

      if (data.status === "paid" && data.commande) {
        setActiveView("normalized");
      }
    } catch (error) {
      console.error("Erreur lors de la récupération de la commande :", error);
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: "Impossible de charger cette vente à crédit.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  const handlePrint = () => {
    const previousTitle = document.title;
    const label =
      activeView === "normalized"
        ? `Facture normalisée ${item.designation}`
        : `Décharge paiement ${selectedPaymentId || item.designation}`;
    document.title = `ASEBât - ${label} - Vente de matériaux de construction`;
    window.print();
    document.title = previousTitle;
  };

  const openPaymentModal = () => {
    setPaymentAmount("");
    setPaymentMethod("Espèces");
    setPaymentNote("");
    setShowPaymentModal(true);
  };

  const handleAddPayment = async () => {
    if (!paymentAmount) return;

    setPaymentLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${APP_API_URL}/installment-orders/${id}/payments`,
        {
          amount: parseFloat(paymentAmount),
          user_id: user.id,
          payment_method: paymentMethod,
          notes: paymentNote,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      setShowPaymentModal(false);
      await fetchData();

      if (response.data?.fully_paid) {
        await Swal.fire({
          icon: "success",
          title: "Commande soldée",
          text: "La facture normalisée a été générée automatiquement.",
          timer: 2500,
          showConfirmButton: false,
        });
      } else {
        await Swal.fire({
          icon: "success",
          title: "Paiement enregistré",
          timer: 1500,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.response?.data?.message || "Impossible d'enregistrer le paiement.",
      });
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleFinalizeSale = async () => {
    const result = await Swal.fire({
      icon: "question",
      title: "Finaliser la vente ?",
      text: "Les produits reviennent dans la caisse. Le client peut les modifier. Les acomptes déjà versés restent sur son solde.",
      showCancelButton: true,
      confirmButtonText: "Ouvrir la caisse",
      cancelButtonText: "Annuler",
    });
    if (!result.isConfirmed) return;

    try {
      await stageInstallmentForCart(id);
      navigate("/commandes");
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.response?.data?.message || error.message || "Impossible de reprendre cette commande.",
      });
    }
  };

  const handleFinalizeInvoice = async () => {
    const result = await Swal.fire({
      icon: "question",
      title: "Générer la facture normalisée ?",
      text: "La commande est soldée. La facture EMCeF sera créée.",
      showCancelButton: true,
      confirmButtonText: "Générer",
      cancelButtonText: "Annuler",
    });

    if (!result.isConfirmed) return;

    setFinalizeLoading(true);
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${APP_API_URL}/installment-orders/${id}/finalize-invoice`,
        { user_id: user.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      await fetchData();
      setActiveView("normalized");

      Swal.fire({
        icon: "success",
        title: "Facture générée",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.response?.data?.message || "Impossible de générer la facture.",
      });
    } finally {
      setFinalizeLoading(false);
    }
  };

  const handleDeleteOrder = async () => {
    const stockDeducted = item.stock_deducted !== false;
    const result = await Swal.fire({
      icon: "warning",
      title: "Supprimer cette commande ?",
      html: stockDeducted
        ? "Le stock des produits sera <strong>restauré</strong>. Les paiements associés seront également supprimés."
        : "Cette commande sera supprimée avec tous les paiements associés.",
      showCancelButton: true,
      confirmButtonText: "Supprimer",
      cancelButtonText: "Annuler",
      confirmButtonColor: "#dc3545",
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${APP_API_URL}/installment-orders/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await Swal.fire({
        icon: "success",
        title: "Supprimée",
        text: response.data?.message || "Commande supprimée avec succès.",
        timer: 2000,
        showConfirmButton: false,
      });

      navigate(listPath);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Erreur",
        text: error.response?.data?.message || "Impossible de supprimer cette commande.",
      });
      setLoading(false);
    }
  };

  const isPaid = item.status === "paid";
  const hasRemaining = Number(item.remaining_amount) > 0;
  const hasNormalizedInvoice = Boolean(item.commande);
  const canFinalize =
    !isRole13 && !hasRemaining && !hasNormalizedInvoice && Number(item.total_paid) > 0;

  if (isLoading) {
    return <Loader />;
  }

  return (
    <OrderLayout document>
      <div className="mx-auto w-full max-w-6xl pb-8">
        {/* Barre d'actions — hors zone imprimable */}
        <div className="no-print sticky top-0 z-10 mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <Link
                to={listPath}
                className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800"
              >
                <i className="bi bi-arrow-left me-1" />
                Retour à la liste
              </Link>
              <h1 className="mt-1 text-lg font-bold text-gray-900">{item.designation}</h1>
              <p className="text-sm text-gray-500">
                {item.customer?.name || "—"} · {STATUS_LABELS[item.status] || item.status}
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              {!isRole13 && hasRemaining && (
                <button
                  type="button"
                  onClick={openPaymentModal}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                >
                  <i className="bi bi-plus-circle me-1" />
                  Ajouter un paiement
                </button>
              )}
              {!isRole13 && hasRemaining && item.status !== "converted" && (
                <button
                  type="button"
                  onClick={handleFinalizeSale}
                  className="inline-flex items-center rounded-md bg-green-700 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-600"
                >
                  <i className="bi bi-cart-check me-1" />
                  Finaliser la vente
                </button>
              )}
              {canFinalize && (
                <button
                  type="button"
                  onClick={handleFinalizeInvoice}
                  disabled={finalizeLoading}
                  className="inline-flex items-center rounded-md bg-green-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-green-500 disabled:opacity-60"
                >
                  {finalizeLoading ? "Génération..." : "Générer la facture normalisée"}
                </button>
              )}
              {!isRole13 && (
                <button
                  type="button"
                  onClick={handleDeleteOrder}
                  className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500"
                >
                  <i className="bi bi-trash me-1" />
                  Annuler
                </button>
              )}
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-3">
            <span className="rounded-full bg-gray-100 px-3 py-1 text-sm">
              Total : <strong>{formatNumber(item.total_amount)} FCFA</strong>
            </span>
            <span className="rounded-full bg-green-50 px-3 py-1 text-sm text-green-800">
              Payé : <strong>{formatNumber(item.total_paid)} FCFA</strong>
            </span>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-sm text-amber-800">
              Reste : <strong>{formatNumber(item.remaining_amount)} FCFA</strong>
            </span>
          </div>

          {hasNormalizedInvoice && (
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => setActiveView("normalized")}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                  activeView === "normalized"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Facture normalisée
              </button>
              <button
                type="button"
                onClick={() => setActiveView("discharge")}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold ${
                  activeView === "discharge"
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
              >
                Décharges de paiement
              </button>
            </div>
          )}
        </div>

        <div className="bg-white p-4 sm:p-6 lg:p-8 rounded-lg shadow-sm">
          {activeView === "normalized" && hasNormalizedInvoice ? (
            <div className="printable w-full">
              <FactureComponent invoiceData={item.commande} isCopy={false} />
              <FactureComponent invoiceData={item.commande} />

              <div className="modal-footer py-4 space-x-4 no-print">
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                >
                  Imprimer la facture normalisée
                </button>
              </div>
            </div>
          ) : (
            <>
              {item.payments && item.payments.length > 0 ? (
                <div className="payment-selector no-print mb-6 flex flex-wrap gap-2">
                  {item.payments
                    .sort((a, b) => new Date(b.payment_date) - new Date(a.payment_date))
                    .map((payment) => (
                      <button
                        key={payment.id}
                        type="button"
                        onClick={() => setSelectedPaymentId(payment.id)}
                        className={`inline-flex items-center rounded-md px-3 py-2 text-sm font-semibold shadow-sm transition-all duration-300 ${
                          selectedPaymentId === payment.id
                            ? "bg-blue-600 text-white hover:bg-blue-500"
                            : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                        }`}
                      >
                        Paiement {payment?.payment_sequence} —{" "}
                        {new Date(payment.payment_date).toLocaleDateString()} ({payment.amount} XOF)
                      </button>
                    ))}
                </div>
              ) : (
                <p className="no-print mb-4 text-gray-600">Aucun paiement enregistré.</p>
              )}

              <div className="printable w-full">
                {selectedPaymentId && (
                  <div className="payment-invoice" data-payment-id={selectedPaymentId}>
                    <InstallmentOrderInvoice
                      item={item}
                      payment={item.payments.find((p) => p.id === selectedPaymentId)}
                      isCopy={false}
                    />
                    <InstallmentOrderInvoice
                      item={item}
                      payment={item.payments.find((p) => p.id === selectedPaymentId)}
                      isCopy={true}
                    />

                    <InstallmentOrderInvoiceMagasinier data={item} />
                    <InstallmentOrderInvoiceMagasinier data={item} />

                    <div className="modal-footer py-4 space-x-4 no-print">
                      <button
                        type="button"
                        onClick={handlePrint}
                        className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-500"
                      >
                        Imprimer cette décharge
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {showPaymentModal && (
        <DuoModal
          title={`Paiement — ${item.designation}`}
          onClose={() => setShowPaymentModal(false)}
          footer={
            <>
              <button
                type="button"
                className="duo-btn duo-btn--ghost"
                onClick={() => setShowPaymentModal(false)}
                disabled={paymentLoading}
              >
                Annuler
              </button>
              <button
                type="button"
                className="duo-btn duo-btn--primary"
                onClick={handleAddPayment}
                disabled={paymentLoading || !paymentAmount}
              >
                {paymentLoading ? "En cours..." : "Valider le paiement"}
              </button>
            </>
          }
        >
          <div className="duo-balance-box mb-3">
            Reste à payer : <strong>{formatNumber(item.remaining_amount)} FCFA</strong>
          </div>
          <div className="duo-form-group">
            <label htmlFor="paymentAmount" className="duo-form-label">
              Montant à payer *
            </label>
            <input
              type="number"
              id="paymentAmount"
              className="duo-input"
              min="0"
              step="1"
              max={item.remaining_amount}
              value={paymentAmount}
              onChange={(e) => setPaymentAmount(e.target.value)}
            />
          </div>
          <div className="duo-form-group">
            <label htmlFor="paymentMethod" className="duo-form-label">
              Méthode de paiement *
            </label>
            <select
              id="paymentMethod"
              className="duo-select"
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
            >
              {PAYMENT_METHODS.map((m) => (
                <option key={m.value} value={m.label}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>
          <div className="duo-form-group mb-0">
            <label htmlFor="paymentNote" className="duo-form-label">
              Note (optionnelle)
            </label>
            <textarea
              id="paymentNote"
              className="duo-input"
              rows={3}
              value={paymentNote}
              onChange={(e) => setPaymentNote(e.target.value)}
              placeholder="Remarque ou commentaire"
            />
          </div>
        </DuoModal>
      )}
    </OrderLayout>
  );
}
